// frontend/src/pages/admin/RoleForm.js
import React, { useState, useEffect, useCallback } from 'react'; 
import { Modal, Form, Input, Button, Checkbox, Table, Space, Badge, Divider } from 'antd'; // Removed 'message' import
import api from '../../utils/api'; 
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'; 
import { useNotificationApi } from '../../context/NotificationContext'; // <--- NEW IMPORT

const { TextArea } = Input;

const RoleForm = ({ visible, onCancel, onSuccess, role }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState([]); 
  const [fetchingUsers, setFetchingUsers] = useState(false); 
  const notificationApi = useNotificationApi(); // <--- NEW: Get notification API from context

  // Helper function to show errors (persistent notification, top-right)
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key); 
    try {
      notificationApi.error({
        key, 
        message: title,
        description: content,
        duration: 4.5, 
        placement: 'topRight',
        style: {
          width: 400, 
          zIndex: 10000, 
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({ 
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]); 

  // Helper function to show success (transient notification, top-right)
  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key, 
      message: title,
      description: description,
      duration: 3, 
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000, 
      },
    });
  }, [notificationApi]);

  // Helper function to show loading (sticky notification, top-right, with delay and min display time)
  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300; 
    const minDisplayTime = 500; 

    const startTime = Date.now();
    
    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0, 
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000, 
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer); 
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);
      
      setTimeout(() => {
        if (dismissNotification) { 
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  // Refetch assigned users for the current role
  const refetchAssignedUsers = useCallback(async () => {
    if (!role || !role.id) {
      // console.warn('refetchAssignedUsers called without a valid role ID.');
      return;
    }
    setFetchingUsers(true);
    try {
      // Re-fetch the specific role to get its latest assignedUsers (from updated backend controller)
      const response = await api.get(`/api/roles/${role.id}`);
      setAssignedUsers(response.data.data.assignedUsers || []);
      console.log('RoleForm: Refetched assigned users:', response.data.data.assignedUsers);
    } catch (error) {
      console.error('Error refetching assigned users for role:', error.response?.data || error.message);
      showError('Failed to Refresh Users', error.response?.data?.message || 'Failed to refresh assigned users for this role.', 'refreshAssignedUsersError'); // Use showError
    } finally {
      setFetchingUsers(false);
    }
  }, [role, showError]); // Added showError to dependencies

  // Effect to populate form when 'role' prop changes (for editing)
  useEffect(() => {
    if (!visible) {
      form.resetFields(); 
      setAssignedUsers([]); 
      return;
    }

    if (role) {
      console.log('RoleForm: Initializing form for editing role:', role);
      form.setFieldsValue({
        name: role.name,
        description: role.description,
        isActive: Boolean(role.isActive), 
      });
      refetchAssignedUsers();
    } else {
      console.log('RoleForm: Resetting form fields for new role.');
      form.resetFields();
      setAssignedUsers([]); 
      form.setFieldsValue({
        isActive: true, 
      });
    }
  }, [visible, role, form, refetchAssignedUsers]); 

  // Handle form submission (add new role or update existing role)
  const handleSubmit = async () => {
    let hideLoading; // Declare hideLoading outside try block
    try {
      const values = await form.validateFields(); 
      setLoading(true);
      const loadingKey = 'roleSubmitLoading';
      hideLoading = showLoading(loadingKey, role ? 'Updating role...' : 'Creating role...'); 

      const payload = {
        name: values.name,
        description: values.description || null, 
        isActive: values.isActive, 
      };

      if (role) {
        console.log('RoleForm: Attempting to update role:', role.id, payload);
        await api.put(`/api/roles/${role.id}`, payload);
        showSuccess('Role Updated', 'Role details updated successfully!', 'roleUpdateSuccess'); // Use showSuccess
      } else {
        console.log('RoleForm: Attempting to create new role:', payload);
        await api.post('/api/roles', payload);
        showSuccess('Role Created', 'New role created successfully!', 'roleCreateSuccess'); // Use showSuccess
      }

      // Introduce a small delay before closing the modal and dismissing loading
      setTimeout(() => {
        if (hideLoading && typeof hideLoading === 'function') hideLoading(); // Dismiss loading notification
        onSuccess(true); // Pass true to indicate it was triggered by an action
        onCancel(); 
      }, 500); 

    } catch (error) {
      if (hideLoading && typeof hideLoading === 'function') hideLoading(); // Dismiss loading on error
      setLoading(false);
      console.error('Error saving role:', error.response?.data || error.message, error);
      showError('Submission Failed', error.response?.data?.message || 'Failed to save role.', 'roleFormSubmitError'); // Use showError
    } finally {
      setLoading(false);
    }
  };

  // Define columns for the assigned users table
  const assignedUsersColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      width: '40%',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
      width: '40%',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Badge
          status={isActive ? 'success' : 'error'}
          text={isActive ? 'Active' : 'Inactive'}
        />
      ),
      width: '20%',
    },
  ];

  return (
    <Modal
      title={role ? 'Edit Role' : 'Add New Role'}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel} disabled={loading}> {/* Disable cancel button while loading */}
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Submit
        </Button>,
      ]}
      width={700} 
      destroyOnClose={true}
    >
      {/* No contextHolderNotification needed here, it's global in App.js */}
      <Form
        form={form}
        layout="vertical"
        name="role_form"
        initialValues={{ isActive: true }}
      >
        <Form.Item
          name="name"
          label="Role Name"
          rules={[
            { required: true, message: 'Please enter the role name!' },
            { type: 'string', min: 2, message: 'Role name must be at least 2 characters.' }
          ]}
        >
          <Input placeholder="e.g., Customer Standard" />
        </Form.Item>
        <Form.Item
          name="description"
          label="Description"
        >
          <TextArea rows={3} placeholder="e.g., Standard access for customer users" />
        </Form.Item>
        <Form.Item
          name="isActive"
          label="Status"
          valuePropName="checked"
        >
          <Checkbox>Active</Checkbox>
        </Form.Item>

        {/* --- Assigned Users Section (Only in Edit Mode) --- */}
        {role && ( 
          <>
            <Divider orientation="left" style={{ marginTop: '30px', marginBottom: '20px' }}>
              Users with This Role
            </Divider>
            <div style={{ marginBottom: '20px' }}>
              <Table
                columns={assignedUsersColumns}
                dataSource={assignedUsers.sort((a,b) => (a.name || '').localeCompare(b.name || ''))}
                rowKey="userId"
                pagination={false} 
                loading={fetchingUsers}
                size="small"
                locale={{ emptyText: 'No active users found with this role.' }}
              />
            </div>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default RoleForm;